import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

class StickyBar extends Component {
	/** @type {string | null} */
	#barType = null;

	/** @type {HTMLElement | null} */
	#parent = null;

	/** @type {HTMLElement | null} */
	#header = null;

	/** @type {boolean} */
	#shouldOverride = false;

	/** @type {boolean} */
	#visible = false;

	/** @type {HTMLElement | null} */
	#trigger = null;

	/** @type {IntersectionObserver | null} */
	#observer = null;

	/** @type {ResizeObserver | null} */
	#headerResizeObserver = null;

	/** @type {number | null} */
	#rafId = null;

	/** @type {number | null} */
	#scrollRafId = null;

	/** @type {(() => void) | null} */
	#onScroll = null;

	/** @type {number | null} */
	#triggerTop = null;

	/** @type {(() => void) | null} */
	#onResize = null;

	/** @type {number} */
	#headerHeight = 0;

	/** @type {HTMLButtonElement | null} */
	#targetAddToCartButton = null;

	/** @type {boolean} */
	#hasScrolled = false;

	/** @type {number} */
	#initialScrollY = 0;

	/** @type {number} */
	#lastScrollY = 0;

	/** @type {number} */
	#lastToggleTime = 0;

	/** @type {AbortController} */
	#abortController = new AbortController();

	/** @type {string[]} */
	#inheritedColorSchemeClasses = [];

	/** @type {MutationObserver | null} */
	#preorderObserver = null;

	/** @type {number | null} */
	#preorderRafId = null;

	async connectedCallback() {
		super.connectedCallback();

		this.#initialScrollY = window.scrollY || window.pageYOffset || 0;
		this.#lastScrollY = this.#initialScrollY;

		this.classList.add('hidden');

		this.#parent = /** @type {HTMLElement | null} */ (this.parentElement);
		if (!this.#parent) return;

		const barType = this.getAttribute('data-bar-type');
		this.#barType = barType ?? 'product_page';

		const overrideHeader = this.getAttribute('data-override-header') ?? 'true';
		if (overrideHeader === 'true') {
			await customElements.whenDefined('header-component');
			this.#header = document.querySelector('header-component');
			this.#shouldOverride = true;
		}

		const inheritColorScheme = this.getAttribute('data-inherit-color-scheme') ?? 'true';
		if (inheritColorScheme === 'true' && this.#header) {
			const headerContainer = this.#header.querySelector('.header__row');
			if (headerContainer) {
				this.#inheritedColorSchemeClasses = Array.from(headerContainer.classList).filter(
					(className) => className.startsWith('color-scheme-')
				);
			}
			this.#applyInheritedColorScheme();
		}

		if (this.#header && this.#shouldOverride) {
			const applyHeaderHeight = () => {
				if (!this.#header) return;
				const height = this.#header.getBoundingClientRect().height;
				if (height > 0) this.#headerHeight = height;
				this.style.setProperty('--header-height', `${this.#headerHeight}px`);
			};

			applyHeaderHeight();

			this.#headerResizeObserver = new ResizeObserver(() => {
				if (this.#rafId != null) cancelAnimationFrame(this.#rafId);
				this.#rafId = requestAnimationFrame(() => {
					this.#rafId = null;
					applyHeaderHeight();
				});
			});

			this.#headerResizeObserver.observe(this.#header);
		}

		const hasAnyTriggers = document.querySelector('[data-sticky-trigger]') != null;

		const scopedTrigger =
			this.#parent.querySelector('[data-sticky-trigger]') ||
			this.closest('[data-section-id]')?.querySelector('[data-sticky-trigger]') ||
			document.querySelector('[data-sticky-trigger]');

		this.#trigger = /** @type {HTMLElement | null} */ (scopedTrigger);
		this.#targetAddToCartButton = this.#getTargetAddToCartButton();

		if (this.#barType === 'product_page') {
			const { signal } = this.#abortController;
			document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
			document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
			document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
			this.#setupPreorderSync();
		}

		if (!hasAnyTriggers) {
			const updateFromScroll = () => {
				if (!this.#hasScrolled) {
					this.#toggleVisibility(false);
					return;
				}

				const y = window.scrollY || window.pageYOffset || 0;
				const scrollingDown = y > this.#lastScrollY;
				this.#lastScrollY = y;

				// Mobile product pages: Add to cart sits well below the gallery, so show the bar once the header is scrolled past
				const mobileProduct = this.#barType === 'product_page' && window.matchMedia('(max-width: 749px)').matches;
				const threshold = mobileProduct
					? this.#headerHeight || 64
					: window.innerHeight || document.documentElement.clientHeight || 0;

				if (this.#visible) {
					this.#toggleVisibility(scrollingDown || y >= threshold - 50);
					return;
				}
				this.#toggleVisibility(scrollingDown && y >= threshold);
			};

			this.#onScroll = () => {
				this.#hasScrolled = this.#didUserScroll();
				if (this.#scrollRafId != null) return;
				this.#scrollRafId = requestAnimationFrame(() => {
					this.#scrollRafId = null;
					updateFromScroll();
				});
			};

			window.addEventListener('scroll', this.#onScroll, { passive: true });
			updateFromScroll();
			return;
		}

		if (!this.#trigger) return;

		const updateThreshold = () => {
			const y = window.scrollY || window.pageYOffset || 0;
			this.#triggerTop = this.#trigger ? this.#trigger.getBoundingClientRect().top + y : null;
		};

		const updateFromTrigger = () => {
			if (this.#triggerTop == null) return;
			if (!this.#hasScrolled) {
				this.#toggleVisibility(false);
				return;
			}

			const y = window.scrollY || window.pageYOffset || 0;
			const scrollingDown = y > this.#lastScrollY;
			this.#lastScrollY = y;

			const headerHeight = this.#headerHeight;
			const showAt = this.#triggerTop - headerHeight;
			const hideAt = showAt - 50;

			if (this.#visible) {
				this.#toggleVisibility(scrollingDown || y >= hideAt);
				return;
			}

			this.#toggleVisibility(scrollingDown && y >= showAt);
		};

		updateThreshold();
		updateFromTrigger();

		this.#onScroll = () => {
			this.#hasScrolled = this.#didUserScroll();
			if (this.#scrollRafId != null) return;
			this.#scrollRafId = requestAnimationFrame(() => {
				this.#scrollRafId = null;
				updateFromTrigger();
			});
		};

		this.#onResize = () => {
			updateThreshold();
			updateFromTrigger();
		};

		window.addEventListener('scroll', this.#onScroll, { passive: true });
		window.addEventListener('resize', this.#onResize, { passive: true });

		this.#observer = new IntersectionObserver(() => {
			updateThreshold();
			updateFromTrigger();
		});

		this.#observer.observe(this.#trigger);
	}

	disconnectedCallback() {
		this.#observer?.disconnect();
		this.#observer = null;

		this.#headerResizeObserver?.disconnect();
		this.#headerResizeObserver = null;

		if (this.#rafId != null) cancelAnimationFrame(this.#rafId);
		this.#rafId = null;

		if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
		this.#onScroll = null;

		if (this.#onResize) window.removeEventListener('resize', this.#onResize);
		this.#onResize = null;

		if (this.#scrollRafId != null) cancelAnimationFrame(this.#scrollRafId);
		this.#scrollRafId = null;

		this.#preorderObserver?.disconnect();
		this.#preorderObserver = null;

		if (this.#preorderRafId != null) cancelAnimationFrame(this.#preorderRafId);
		this.#preorderRafId = null;

		this.#abortController.abort();

		super.disconnectedCallback();
	}

	/** @param {boolean} nextVisible */
	#toggleVisibility(nextVisible) {
		if (this.#visible === nextVisible) return;

		const now = performance.now();
		if (now - this.#lastToggleTime < 150) return;
		this.#lastToggleTime = now;

		this.#visible = nextVisible;

		this.classList.toggle('hidden', !nextVisible);
		if (this.#shouldOverride) this.#header?.classList.toggle('hidden', nextVisible);

		this.toggleAttribute('data-visible', nextVisible);
	}

	/**
	 * Handles the sticky bar add-to-cart button click.
	 * Puppet-clicks the main product form's submit button so the main form handles
	 * the /cart/add fetch, cart-drawer opening, and animations — we inherit all of that.
	 */
	handleAddToCartClick = () => {
		if (this.#barType !== 'product_page') return;

		const targetButton = this.#targetAddToCartButton || this.#getTargetAddToCartButton();
		if (!targetButton || targetButton.disabled) return;

		this.#targetAddToCartButton = targetButton;
		targetButton.dataset.puppet = 'true';
		targetButton.click();
	};

	/**
	 * Handles sticky variant select change by driving the main variant-picker,
	 * which in turn dispatches VariantUpdateEvent (caught by #handleVariantUpdate).
	 * @param {Event} event
	 */
	handleVariantChange = (event) => {
		const select = /** @type {HTMLSelectElement | null} */ (event.target);
		if (!(select instanceof HTMLSelectElement)) return;

		const selectedOption = select.options[select.selectedIndex];
		if (!selectedOption) return;

		/** @type {string[]} */
		let optionValues;
		try {
			optionValues = JSON.parse(selectedOption.dataset.options || '[]');
		} catch {
			return;
		}

		const wrapper = this.querySelector('[data-product-id]');
		const productId = wrapper instanceof HTMLElement ? wrapper.dataset.productId : null;
		if (!productId) return;

		const mainPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
		if (!mainPicker) return;

		const mainSelects = /** @type {HTMLSelectElement[]} */ (
			Array.from(mainPicker.querySelectorAll('select.variant-option__select'))
		);
		const mainFieldsets = /** @type {HTMLFieldSetElement[]} */ (
			Array.from(mainPicker.querySelectorAll('fieldset[data-fieldset-index]'))
		);

		optionValues.forEach((value, idx) => {
			if (mainSelects.length > 0) {
				const target = mainSelects[idx];
				if (!target || target.value === value) return;
				target.value = value;
				target.dispatchEvent(new Event('change', { bubbles: true }));
			} else if (mainFieldsets.length > 0) {
				const target = mainFieldsets[idx];
				if (!target) return;
				const radio = /** @type {HTMLInputElement | null} */ (
					target.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`)
				);
				if (!radio || radio.checked) return;
				radio.click();
			}
		});
	};

	/**
	 * Syncs sticky bar content when main variant picker updates.
	 * @param {Event} event
	 */
	#handleVariantUpdate = (event) => {
		const detail = /** @type {any} */ (event).detail;
		const html = detail?.data?.html;
		if (!html) return;

		const newStickyBar = html.querySelector('sticky-bar[data-bar-type="product_page"]');
		if (!newStickyBar) return;

		morph(this, newStickyBar, { childrenOnly: true });

		// Morph replaces the wrapper node, so re-apply the runtime color-scheme classes.
		this.#applyInheritedColorScheme();

		// Re-cache main button — morph may swap nodes on the page.
		this.#targetAddToCartButton = this.#getTargetAddToCartButton();

		// Morph rebuilds the sticky button node, resetting any inline hide we applied.
		this.#applyPreorderState();
	};

	#applyInheritedColorScheme() {
		if (this.#inheritedColorSchemeClasses.length === 0) return;
		const wrapper = this.firstElementChild;
		if (!wrapper) return;
		for (const className of this.#inheritedColorSchemeClasses) {
			wrapper.classList.add(className);
		}
	}

	/**
	 * Resets the puppet flag on the main button after a cart add settles.
	 */
	#handleCartAddComplete = () => {
		if (this.#targetAddToCartButton) {
			this.#targetAddToCartButton.dataset.puppet = 'false';
		}
	};

	/**
	 * Preorder apps inject a `.rez-preorder-btn` onto the product page — often
	 * after load, with its "Preorder" label set asynchronously. While that button
	 * is present and reads "Preorder", the sticky bar's add-to-cart button is
	 * meaningless (it puppet-clicks the main form button, which the app overrides),
	 * so we hide it. A MutationObserver covers the async injection / label change.
	 */
	#setupPreorderSync() {
		this.#applyPreorderState();

		this.#preorderObserver = new MutationObserver(this.#schedulePreorderSync);
		this.#preorderObserver.observe(document.body, {
			subtree: true,
			childList: true,
			characterData: true,
		});
	}

	#schedulePreorderSync = () => {
		if (this.#preorderRafId != null) return;
		this.#preorderRafId = requestAnimationFrame(() => {
			this.#preorderRafId = null;
			this.#applyPreorderState();
		});
	};

	#applyPreorderState = () => {
		const stickyButton = /** @type {HTMLElement | null} */ (this.querySelector('.sticky-bar__button'));
		if (!stickyButton) return;

		const preorderButton = document.querySelector('.rez-preorder-btn');
		const isPreorder =
			preorderButton != null && (preorderButton.textContent ?? '').trim().toLowerCase() === 'preorder';

		const next = isPreorder ? 'none' : '';
		if (stickyButton.style.display !== next) stickyButton.style.display = next;
	};

	/**
	 * Finds the main product add-to-cart button.
	 * @returns {HTMLButtonElement | null}
	 */
	#getTargetAddToCartButton() {
		const button = document.querySelector('product-form-component [ref="addToCartButton"]');
		return /** @type {HTMLButtonElement | null} */ (button);
	}

	/**
	 * Theme editor can fire an initial programmatic scroll event on load.
	 * Keep the bar hidden until scroll position changes from its initial value.
	 * @returns {boolean}
	 */
	#didUserScroll() {
		const currentY = window.scrollY || window.pageYOffset || 0;
		return Math.abs(currentY - this.#initialScrollY) > 2;
	}
}

if (!customElements.get('sticky-bar')) {
	customElements.define('sticky-bar', StickyBar);
}
